import { configureStore } from '@reduxjs/toolkit';

import { RoutingState, setterRouting } from '../store/slices/routingSlice';

const GantUrlParams = ['period', 'mode'] as const;
const CalendarUrlParams = ['clds', 'cldd'] as const;
const TableUrlParams = ['page', 'pageSize'] as const;

type StoreType = ReturnType<typeof configureStore<{ routing: RoutingState }>>;

export type KnownScheduleUrl =
    | '/schedule/table'
    | '/schedule/table/items'
    | '/schedule/table/tasks'
    | '/schedule/timeline'
    | '/schedule/calendar'
    | '/schedule/task-board';

const knownSharedParamsArray = ['item', 'task', 'gplist', 'gstatus', ...CalendarUrlParams] as const;
const knownSharedParams = new Set(knownSharedParamsArray as unknown as string[]);

const _knownParams = [...GantUrlParams, ...knownSharedParamsArray, ...TableUrlParams] as const;

type UrlParamValue = string | number | boolean | undefined;
type UrlParams = Record<(typeof _knownParams)[number], UrlParamValue>;

const urlParamsFilter: Record<KnownScheduleUrl, Set<string>> = {
    '/schedule/table': new Set<string>(TableUrlParams),
    '/schedule/table/items': new Set<string>(TableUrlParams),
    '/schedule/table/tasks': new Set<string>(TableUrlParams),
    '/schedule/timeline': new Set<string>(GantUrlParams),
    '/schedule/calendar': new Set<string>(CalendarUrlParams),
    '/schedule/task-board': new Set<string>(),
};

const navigateInternal = (store: StoreType, newUrl: KnownScheduleUrl) => {
    const { routing } = store.getState();
    const routeParams = routing[newUrl] ?? {};
    const sharedParams = routing.shared ?? {};

    const searchParams = { ...routeParams, ...sharedParams };

    const url = new URL(newUrl, window.location.origin);

    url.search = new URLSearchParams(searchParams).toString();
    window.history.pushState({}, '', url);
};

const setUrlParamInternal = (store: StoreType, params: Partial<UrlParams>) => {
    const url = new URL(window.location.href);

    const sharedParams = { ...(store.getState().routing.shared ?? {}) };

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined) {
            url.searchParams.delete(key);
            if (knownSharedParams.has(key)) {
                delete sharedParams[key];
            }
        } else {
            url.searchParams.set(key, String(value));
        }
    });

    const routeParams: Record<string, string> = {};
    url.searchParams.forEach((value, key) => {
        const paramsFilter = urlParamsFilter[url.pathname as KnownScheduleUrl];
        if (paramsFilter?.has(key)) {
            routeParams[key] = value;
        }
        if (knownSharedParams.has(key)) {
            sharedParams[key] = value;
        }
    });

    url.search = new URLSearchParams({ ...routeParams, ...sharedParams }).toString();

    store.dispatch(
        setterRouting({
            [url.pathname]: routeParams,
        }),
    );

    store.dispatch(
        setterRouting({
            shared: sharedParams,
        }),
    );

    window.history.pushState({}, '', url);
};

export const getUseRouting = (store: StoreType) => {
    function navigate(newUrl: KnownScheduleUrl) {
        return navigateInternal(store, newUrl);
    }

    function setUrlParam(params: Partial<UrlParams>) {
        return setUrlParamInternal(store, params);
    }

    function getUrlPath() {
        return new URL(window.location.href).pathname;
    }

    function getParams(type: 'initial' | 'current' = 'current') {
        const url = type === 'initial' ? store.getState().routing.initial.url : window.location.href;
        return Object.fromEntries(new URL(url).searchParams.entries()) as Partial<UrlParams>;
    }

    return {
        navigate,
        setUrlParam,
        getUrlPath,
        getParams,
    };
};

export const setInitialRoutingState = () => {
    const url = new URL(window.location.href);

    const routeParams: Record<string, string> = {};
    const sharedParams: Record<string, string> = {};

    url.searchParams.forEach((value, key) => {
        const paramsFilter = urlParamsFilter[url.pathname as KnownScheduleUrl];
        if (paramsFilter?.has(key)) {
            routeParams[key] = value;
        }
        if (knownSharedParams.has(key)) {
            sharedParams[key] = value;
        }
    });

    return {
        initial: { url: window.location.href },
        shared: sharedParams,
        [url.pathname]: routeParams,
    };
};
